import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Chip, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { StyledMenu } from '@ui/components/common/Menu';
import { MoreHoriz } from '@mui/icons-material';
import EditCompanyDialog from './EditCompanyDialog';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { Iconly } from 'react-iconly';
import { Delete } from 'react-iconly';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAllCompaniesAsync, updateCompanyAsync, deleteCompanyAsync } from '@application/features/company/companyThunk';
import { APIStatus } from '@application/enums/api-status';
import { CompanyRegion } from '@application/enums/company-region-enum';
const mapRecordToItem = (record) => ({
    id: record.id,
    name: record.name,
    displayName: record.name,
    bvName: '',
    legalGroup: record.legalGroup ?? '',
    region: record.region != null ? CompanyRegion[record.region] : '',
    status: record.isActive ? 'active' : 'inactive',
    companyGroup: '',
    country: '',
    email: record.email,
    phone: record.phoneNo,
    logoUrl: record.logoUrl ?? undefined
});
const CompaniesTab = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useDispatch();
    const { allCompanies, status, updateStatus } = useSelector((state) => state.company);
    const [searchValue, setSearchValue] = useState('');
    const [filters, setFilters] = useState({});
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    useEffect(() => {
        dispatch(getAllCompaniesAsync());
    }, [dispatch]);
    useEffect(() => {
        if (updateStatus === APIStatus.SUCCESS) {
            setEditDialogOpen(false);
        }
    }, [updateStatus]);
    const companies = useMemo(() => allCompanies.map(mapRecordToItem), [allCompanies]);
    const filteredCompanies = useMemo(() => {
        const search = searchValue.trim().toLowerCase();
        return companies.filter((company) => {
            const matchesSearch = search.length === 0 ||
                company.name.toLowerCase().includes(search) ||
                company.displayName.toLowerCase().includes(search) ||
                company.bvName.toLowerCase().includes(search) ||
                company.legalGroup.toLowerCase().includes(search);
            const matchesRegion = !filters.region || company.region === filters.region;
            const matchesStatus = !filters.status || company.status === filters.status;
            return matchesSearch && matchesRegion && matchesStatus;
        });
    }, [companies, filters, searchValue]);
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const rowMenuActions = [
        {
            id: 'edit-company',
            text: t('actions.edit'),
            icon: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }),
            to: () => {
                handleMenuClose();
                setEditDialogOpen(true);
            }
        },
        {
            id: 'delete-company',
            text: t('actions.delete'),
            icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
            color: theme.palette.error.light,
            to: () => {
                handleMenuClose();
                setDeleteConfirmationOpen(true);
            }
        }
    ];
    const columns = [
        {
            field: 'name',
            headerName: t('settings.companyManagement.name'),
            flex: 1,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Avatar, { src: params.row.logoUrl, sx: {
                            width: 24,
                            height: 24,
                            '& .MuiAvatar-img': {
                                objectFit: 'contain'
                            }
                        }, children: String(params.value || '')
                            .charAt(0)
                            .toUpperCase() }), _jsx(Typography, { variant: 'body2', children: params.value })] }))
        },
        {
            field: 'legalGroup',
            headerName: t('settings.companyManagement.legalGroup'),
            flex: 1,
            minWidth: 200,
            sortable: false
        },
        {
            field: 'region',
            type: 'string',
            headerName: t('settings.companyManagement.region'),
            flex: 0.8,
            minWidth: 120,
            sortable: false
        },
        {
            field: 'status',
            type: 'string',
            headerName: t('settings.companyManagement.status'),
            minWidth: 130,
            sortable: false,
            renderCell: (params) => (_jsx(Chip, { size: 'small', label: params.value === 'active'
                    ? t('settings.companyManagement.active')
                    : t('settings.companyManagement.inactive'), color: params.value === 'active' ? 'success' : 'default', variant: 'soft' }))
        },
        {
            field: 'actions',
            headerName: t('settings.companyManagement.action'),
            minWidth: 90,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (_jsx(IconButton, { size: 'small', onClick: (event) => {
                    event.stopPropagation();
                    setMenuAnchorEl(event.currentTarget);
                    setMenuRow(params.row);
                }, children: _jsx(MoreHoriz, {}) }))
        }
    ];
    const handleUpdateCompany = (updatedCompany) => {
        dispatch(updateCompanyAsync({
            id: updatedCompany.id,
            name: updatedCompany.name,
            email: updatedCompany.email,
            phoneNo: updatedCompany.phone,
            region: (CompanyRegion[updatedCompany.region] ??
                CompanyRegion.Unknown),
            logo: updatedCompany.logoFile ?? null,
            legalGroup: updatedCompany.legalGroup ?? null
        }));
        setMenuRow(updatedCompany);
    };
    const handleDeleteCompany = () => {
        if (!menuRow?.id)
            return;
        const id = menuRow.id;
        setDeleteConfirmationOpen(false);
        setMenuRow(null);
        dispatch(deleteCompanyAsync(id))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message || t('settings.companyManagement.companyDeleted'));
                dispatch(getAllCompaniesAsync());
            }
            else {
                toast.error(response.message || t('settings.companyManagement.failedToDeleteCompany'));
            }
        })
            .catch((error) => {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || t('settings.companyManagement.failedToDeleteCompany'));
        });
    };
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { rows: filteredCompanies, columns: columns, loading: status === APIStatus.PENDING, showHeaderControls: false, showNewHeaderControls: true, disableRowSelectionOnClick: true, maxHeight: 'calc(100vh - 260px)', filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, onApplyFilters: (newFilters) => setFilters(newFilters), resetFiltersKey: resetFiltersKey, filterOptions: {
                    region: Object.keys(CompanyRegion)
                        .filter((key) => isNaN(Number(key)) && key !== 'Unknown')
                        .map((key) => ({ value: key, label: key })),
                    status: [
                        { value: 'active', label: t('settings.companyManagement.active') },
                        { value: 'inactive', label: t('settings.companyManagement.inactive') }
                    ]
                }, noRowsText: t('settings.companyManagement.noCompaniesFound'), customButton: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, children: [_jsx(Box, { children: _jsxs(Typography, { variant: 'h4', children: [' ', filteredCompanies.length, " ", t('settings.companies')] }) }), _jsx(SearchFilter, { forceDesktopVariant: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('settings.companyManagement.searchPlaceholder'), onClear: () => setSearchValue(''), onFilterClick: () => setGridFilterOpen(true), showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                setFilters({});
                                setResetFiltersKey((key) => key + 1);
                            }, filterLabel: t('settings.companyManagement.filters'), widthBox: '220px', isDatagrid: true, iconButtonSize: 'medium', heightBox: '42px' })] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(EditCompanyDialog, { open: editDialogOpen, company: menuRow, onClose: () => setEditDialogOpen(false), onSave: handleUpdateCompany }), deleteConfirmationOpen &&
                (isMobile ? (_jsx(BDrawer, { open: deleteConfirmationOpen, onClose: () => setDeleteConfirmationOpen(false), title: t('common.deleteConfirmation'), dialogActions: _jsxs(Box, { sx: { display: 'flex', width: '100%', gap: 1 }, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setDeleteConfirmationOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleDeleteCompany, sx: { backgroundColor: theme.palette.error.main }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) })) : (_jsx(CustomDialog, { open: deleteConfirmationOpen, title: t('common.deleteConfirmation'), onClose: () => setDeleteConfirmationOpen(false), actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: { display: 'flex', width: '100%', gap: 1 }, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setDeleteConfirmationOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleDeleteCompany, sx: { backgroundColor: theme.palette.error.main }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) })))] }));
};
export default CompaniesTab;
